// ─────────────────────────────────────────────────────────────────
// Painel "veículo em trânsito" — HUD compacto do LiveRouteMonitor.
// ─────────────────────────────────────────────────────────────────
// Versão 2 — mostra o veículo REAL (mesmo ícone que o mapa usa) com
// info operacional rica: matrícula, condutor, morada actual, velocidade,
// próxima paragem. Semáforo de estado: verde (movimento), amarelo
// (ralenti — ignição ligada mas parado), vermelho (parado).
//
// Posicionado no canto inferior ESQUERDO no LiveRouteMonitor.

import React, { useState, useEffect } from 'react';
import { Box, Typography } from '@material-ui/core';
import {
  vehicleIconCategory,
  getVehicleIconDataUrl,
  onVehicleIconLoaded,
} from '../../utils/vehicleIcons';

// Estado do veículo a partir do speed/ignition — igual aos cluster donuts.
function deriveStatus({ speed, ignition }) {
  const v = Math.max(0, Number(speed) || 0);
  if (v > 3) return { key: 'moving', color: '#16a34a', label: 'Em movimento' };
  if (ignition) return { key: 'idle', color: '#eab308', label: 'Ao ralenti' };
  return { key: 'stopped', color: '#ef4444', label: 'Parado' };
}

export default function DriverFirstPersonView({
  vehicle,           // objecto do veículo (opcional — type, plate, attrs)
  plate,             // matrícula em fallback se não vier vehicle.plate
  driverName,        // nome do condutor (tacógrafo / RFID)
  speed,             // km/h
  ignition,          // boolean (do attr io67/io66)
  heading,           // 0-360 (rumo)
  address,           // morada actual (reverse-geocoded pelo Traccar)
  isLive,            // true se GPS recente
  nextStop,          // nome da próxima OT
  nextStopAddr,      // morada da próxima OT
  delivered,         // OTs entregues
  total,             // OTs totais
  source,            // 'traccar' | 'app' | etc.
}) {
  const speedKmh = Math.max(0, Math.round(Number(speed) || 0));
  const status = deriveStatus({ speed: speedKmh, ignition });
  const cat = vehicleIconCategory(vehicle?.type);
  // Cor do ícone segue o estado (igual ao marcador no mapa).
  const iconColor = !isLive ? 'gray' : status.key === 'moving' ? 'green' : status.key === 'idle' ? 'yellow' : 'red';
  const iconUrl = getVehicleIconDataUrl(cat, iconColor);
  // Re-render quando o PNG do ícone acabar de carregar (cache hot path).
  const [, setTick] = useState(0);
  useEffect(() => onVehicleIconLoaded(() => setTick(t => t + 1)), []);

  const hdg = ((Number(heading) || 0) % 360 + 360) % 360;
  const plateText = (plate || vehicle?.plate || '').toUpperCase() || '—';
  const pct = (Number.isFinite(total) && total > 0)
    ? Math.round(((delivered || 0) / total) * 100)
    : null;

  // Cadência da estrada que corre no rodapé — quanto mais depressa, mais
  // curto o ciclo. Visível mas não distractiva: 0.4 s (rápido) a 2 s (lento).
  const roadDur = status.key === 'moving' && isLive
    ? Math.max(0.4, Math.min(2, 60 / Math.max(5, speedKmh)))
    : 0;

  return (
    <Box style={{
      position: 'relative', width: '100%', height: '100%',
      background: '#ffffff',
      display: 'flex', flexDirection: 'column',
      fontFamily: '-apple-system,BlinkMacSystemFont,Segoe UI,Roboto,sans-serif',
    }}>
      <style>{`
        @keyframes hudPulse { 0%,100% { box-shadow: 0 0 0 0 rgba(22,163,74,0.55); } 70% { box-shadow: 0 0 0 8px rgba(22,163,74,0); } }
        @keyframes hudIconBob { 0%,100% { transform: translateY(0); } 50% { transform: translateY(-1.5px); } }
        /* Estrada em primeira pessoa — faixas tracejadas correm "para a
           câmara" (translateY positivo, porque a textura está num plano
           inclinado para trás). 60px = 1 ciclo de tracejado. */
        @keyframes hudRoadDash { from { background-position-y: 0; } to { background-position-y: 60px; } }
      `}</style>

      {/* ── Header: ícone real + matrícula + semáforo ── */}
      <Box style={{
        display: 'flex', alignItems: 'center', gap: 8,
        padding: '8px 10px',
        borderBottom: '1px solid #e2e8f0',
      }}>
        {/* Ícone real do veículo (mesmo que o mapa usa) */}
        <Box style={{
          width: 42, height: 42, borderRadius: 8,
          background: '#f8fafc', border: '1px solid #e2e8f0',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          flexShrink: 0,
        }}>
          {iconUrl ? (
            <img
              src={iconUrl}
              alt={plateText}
              style={{
                width: 32, height: 32, objectFit: 'contain',
                transform: `rotate(${hdg}deg)`,
                animation: status.key === 'moving' ? 'hudIconBob 0.7s ease-in-out infinite' : 'none',
              }}
            />
          ) : (
            <Box style={{
              width: 14, height: 14, borderRadius: '50%',
              background: status.color,
            }} />
          )}
        </Box>
        <Box style={{ flex: 1, minWidth: 0 }}>
          <Box style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            <Typography style={{ fontSize: '0.82rem', fontWeight: 800, color: '#0f172a', letterSpacing: '0.02em' }}>
              {plateText}
            </Typography>
            {/* Semáforo */}
            <Box title={status.label} style={{
              width: 10, height: 10, borderRadius: '50%',
              background: !isLive ? '#94a3b8' : status.color,
              boxShadow: status.key === 'moving' && isLive ? `0 0 6px ${status.color}` : 'none',
              flexShrink: 0,
              animation: status.key === 'moving' && isLive ? 'hudPulse 1.4s ease-out infinite' : 'none',
            }} />
          </Box>
          {driverName && (
            <Typography style={{ fontSize: '0.62rem', fontWeight: 600, color: '#475569', lineHeight: 1.2, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              👤 {driverName}
            </Typography>
          )}
        </Box>
        {/* Velocidade */}
        <Box style={{
          flexShrink: 0,
          display: 'flex', flexDirection: 'column', alignItems: 'flex-end',
          minWidth: 50,
        }}>
          <Typography style={{
            fontSize: '1rem', fontWeight: 800, lineHeight: 1,
            color: !isLive ? '#94a3b8' : status.color,
            fontVariantNumeric: 'tabular-nums',
          }}>
            {speedKmh}
          </Typography>
          <Typography style={{ fontSize: '0.5rem', fontWeight: 700, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.08em' }}>
            km/h
          </Typography>
        </Box>
      </Box>

      {/* ── Estado + morada actual ── */}
      <Box style={{
        padding: '6px 10px',
        background: 'linear-gradient(180deg, rgba(241,245,249,0.5), rgba(255,255,255,0))',
        borderBottom: '1px solid #f1f5f9',
      }}>
        <Typography style={{
          fontSize: '0.56rem', fontWeight: 800,
          color: !isLive ? '#94a3b8' : status.color,
          textTransform: 'uppercase', letterSpacing: '0.06em',
          marginBottom: 2,
        }}>
          {!isLive ? 'Sem GPS recente' : status.label}
          {source && source !== 'traccar' && (
            <span style={{ color: '#94a3b8', marginLeft: 4, fontWeight: 600 }}>· {source}</span>
          )}
        </Typography>
        <Typography style={{
          fontSize: '0.62rem', color: '#475569', lineHeight: 1.3,
          overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
        }} title={address || ''}>
          📍 {address || 'Morada indisponível'}
        </Typography>
      </Box>

      {/* ── Próxima paragem + progresso ── */}
      <Box style={{
        flex: 1, padding: '6px 10px',
        display: 'flex', flexDirection: 'column', justifyContent: 'space-between',
        minHeight: 0, overflow: 'hidden',
      }}>
        <Box style={{ minHeight: 0 }}>
          <Typography style={{ fontSize: '0.52rem', fontWeight: 700, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.06em', lineHeight: 1.2 }}>
            Próxima paragem
          </Typography>
          {nextStop ? (
            <>
              <Typography style={{ fontSize: '0.7rem', fontWeight: 800, color: '#0f172a', lineHeight: 1.25, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {nextStop}
              </Typography>
              {nextStopAddr && nextStopAddr !== nextStop && (
                <Typography style={{ fontSize: '0.56rem', color: '#64748b', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {nextStopAddr}
                </Typography>
              )}
            </>
          ) : (
            <Typography style={{ fontSize: '0.66rem', fontWeight: 700, color: '#16a34a' }}>
              ✓ Todas as paragens concluídas
            </Typography>
          )}
        </Box>
        {Number.isFinite(total) && total > 0 && (
          <Box style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: 4 }}>
            <Box style={{
              flex: 1, height: 4, borderRadius: 2, background: '#e2e8f0', overflow: 'hidden',
            }}>
              <Box style={{
                height: '100%',
                width: `${pct || 0}%`,
                background: 'linear-gradient(90deg, #16a34a, #22c55e)',
                transition: 'width 0.5s ease',
              }} />
            </Box>
            <Typography style={{
              fontSize: '0.6rem', fontWeight: 800, color: '#15803d',
              fontVariantNumeric: 'tabular-nums', minWidth: 38, textAlign: 'right',
            }}>
              {delivered || 0}/{total}
            </Typography>
          </Box>
        )}
      </Box>

      {/* ── Estrada em primeira pessoa no rodapé ──
          Trapézio em perspectiva (CSS 3D) — o plano da estrada está
          inclinado para trás, dando profundidade. Asfalto cinzento
          escuro com vinheta lateral (estreita atrás), faixa central
          tracejada amarela a deslizar do "horizonte" para a "câmara",
          e uma faixa vermelha subtil indicando o trajeto percorrido.
          Cadência proporcional à velocidade — pára quando o veículo
          está parado ou sem GPS. */}
      <Box style={{
        flexShrink: 0, height: 70, position: 'relative', overflow: 'hidden',
        background: '#ffffff',
        borderTop: '1px solid #e2e8f0',
        perspective: '200px',
        perspectiveOrigin: '50% 0%',
      }}>
        {/* O plano da estrada — rotacionado para "deitar" e dar
            sensação de profundidade. Asfalto cinzento claro sobre fundo
            branco. Mais alto que o container e puxado para cima para
            preencher o trapézio resultante. */}
        <Box style={{
          position: 'absolute',
          top: -8, left: '-10%', right: '-10%', height: 190,
          transformOrigin: '50% 0%',
          transform: 'rotateX(58deg)',
          background: 'linear-gradient(180deg, #e5e9ef 0%, #cbd3dd 60%, #b6c0cc 100%)',
        }}>
          {/* Faixa central tracejada amarela */}
          <Box style={{
            position: 'absolute', top: 0, bottom: 0,
            left: '50%', width: 6, transform: 'translateX(-50%)',
            backgroundImage: 'repeating-linear-gradient(180deg, #fbbf24 0 22px, transparent 22px 60px)',
            backgroundSize: '100% 60px',
            backgroundRepeat: 'repeat-y',
            animation: roadDur > 0 ? `hudRoadDash ${roadDur}s linear infinite` : 'none',
            opacity: !isLive ? 0.25 : 0.95,
            filter: 'drop-shadow(0 0 2px rgba(251,191,36,0.6))',
          }} />
          {/* Faixa "trajeto" — fina vermelha à direita do meio (estilo
              da imagem de referência do cliente). Mostra que estamos a
              andar sobre o caminho. */}
          <Box style={{
            position: 'absolute', top: 0, bottom: 0,
            left: 'calc(50% + 18px)', width: 22,
            background: 'linear-gradient(180deg, rgba(239,68,68,0.0) 0%, rgba(239,68,68,0.65) 35%, rgba(220,38,38,0.85) 100%)',
            opacity: !isLive ? 0.15 : 0.9,
          }} />
          {/* Bermas brancas — finas, dos dois lados */}
          <Box style={{
            position: 'absolute', top: 0, bottom: 0, left: '14%', width: 2,
            background: 'rgba(255,255,255,0.55)',
          }} />
          <Box style={{
            position: 'absolute', top: 0, bottom: 0, right: '14%', width: 2,
            background: 'rgba(255,255,255,0.55)',
          }} />
        </Box>
        {/* Vinheta clara — funde os extremos da estrada com o fundo branco */}
        <Box style={{
          position: 'absolute', inset: 0, pointerEvents: 'none',
          background: 'radial-gradient(ellipse 85% 120% at 50% 95%, transparent 45%, rgba(255,255,255,0.85) 100%)',
        }} />
        {/* Horizonte — gradiente branco em cima para fundir com o fundo */}
        <Box style={{
          position: 'absolute', top: 0, left: 0, right: 0, height: 12,
          background: 'linear-gradient(180deg, #ffffff 0%, transparent 100%)',
          pointerEvents: 'none',
        }} />
      </Box>
    </Box>
  );
}
